<script setup lang="ts">
import { Button } from '~ds/components/ui/button';
import { Heading, Text } from '~ds/components/ui/typography';
import UniCredenza from '~kui/components/KUI/Unified/Credenza/UniCredenza.vue';
import UniCredenzaBody from '~kui/components/KUI/Unified/Credenza/UniCredenzaBody.vue';
import UniCredenzaContent from '~kui/components/KUI/Unified/Credenza/UniCredenzaContent.vue';
import UniCredenzaHeader from '~kui/components/KUI/Unified/Credenza/UniCredenzaHeader.vue';
import UniCredenzaTitle from '~kui/components/KUI/Unified/Credenza/UniCredenzaTitle.vue';
import { useMotionPresets } from '~kui/composables/useMotionPresets';
import { motion } from 'motion-v';
import { useI18n } from 'vue-i18n';

export interface Props {
  open: boolean;
  prizeName: string;
  termsTo: string;
  loading?: boolean;
  error?: string | null;
  /** Game-of-skill question the entrant must answer; the confirm CTA stays disabled until answered. */
  skillQuestion?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  error: null,
  skillQuestion: null,
});

defineEmits<{
  'update:open': [value: boolean];
  'confirmEntry': [];
  'cancel': [];
}>();

const skillAnswer = defineModel<string>('skillAnswer', { default: '' });

const SKILL_ANSWER_MAX_LENGTH = 500;

const canConfirm = computed(() => !props.skillQuestion || skillAnswer.value.trim().length > 0);

const { t } = useI18n();
const { SLIDE_UP, TRANSITION } = useMotionPresets();

const termsLinkText = computed(() => t('unified.activity.completion.prize.confirmTermsLink'));

const confirmBodyParts = computed(() => {
  const full = t('unified.activity.completion.prize.confirmBody', {
    termsLink: '{termsLink}',
    prizeName: props.prizeName,
  });
  const [before = '', after = ''] = full.split('{termsLink}');
  return { before, after };
});
</script>

<template>
  <UniCredenza
    :open="open"
    :dismissable="false"
    show-close-button
    @update:open="$emit('update:open', $event)"
  >
    <UniCredenzaContent>
      <UniCredenzaHeader class="items-center text-center">
        <UniCredenzaTitle as-child>
          <Heading
            size="md"
            as="h2"
            class="mx-auto max-w-xs leading-tight sm:text-2xl"
          >
            {{ t('unified.activity.completion.prize.confirmHeading') }}
          </Heading>
        </UniCredenzaTitle>
      </UniCredenzaHeader>

      <UniCredenzaBody>
        <motion.div
          v-bind="SLIDE_UP"
          :transition="TRANSITION"
          class="flex flex-col items-center gap-6 text-center pb-8"
        >
          <Text>
            {{ confirmBodyParts.before }}
            <NuxtLink
              :to="termsTo"
              class="underline font-semibold"
              external
              target="_blank"
            >
              {{ termsLinkText }}
            </NuxtLink>
            {{ confirmBodyParts.after }}
          </Text>

          <div
            v-if="skillQuestion"
            class="flex w-full flex-col gap-2 text-center"
          >
            <Text weight="semibold">
              {{ skillQuestion }}
            </Text>
            <KUICoreInputTextarea
              v-model="skillAnswer"
              :label="t('unified.activity.completion.prize.skillAnswerLabel')"
              hide-label
              :placeholder="t('unified.activity.completion.prize.skillAnswerPlaceholder')"
              :max-length="SKILL_ANSWER_MAX_LENGTH"
              :disabled="loading"
              data-test-id="skill-answer-input"
            />
          </div>

          <Text
            v-if="error"
            class="text-destructive"
          >
            {{ error }}
          </Text>

          <div class="flex w-fit flex-col items-center gap-3">
            <Button
              variant="secondary"
              size="default"
              :disabled="loading || !canConfirm"
              type="button"
              class="w-full"
              @click="$emit('confirmEntry')"
            >
              {{ t('unified.activity.completion.prize.confirmButton') }}
            </Button>
            <button
              type="button"
              class="cursor-pointer text-xs underline"
              :disabled="loading"
              @click="$emit('cancel')"
            >
              {{ t('unified.activity.completion.prize.cancelButton') }}
            </button>
          </div>
        </motion.div>
      </UniCredenzaBody>
    </UniCredenzaContent>
  </UniCredenza>
</template>
